import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useTheme } from "../context/ThemeContext";

type ResponsiveWidths = {
  [breakpoint: number]: { [colIndex: number]: string };
};

type ZebraColors = { odd: string; even: string };

type TableProps = {
  selectedTextWhite?: boolean;
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
  columnWidths?: string[];
  responsiveWidths?: ResponsiveWidths;

  onDoubleClick?: (
    viewIndex: number,
    rowData: (string | number | React.ReactNode)[],
    originalIndex?: number
  ) => void;

  filters?: string[];
  onFiltersChange?: (filters: string[]) => void;

  isTableStandby?: boolean;
  osColumnIndex?: number;

  enableCellColors?: boolean;
  getCellStyle?: (
    rowIndex: number,
    colIndex: number,
    value: string | number | React.ReactNode,
    originalIndex: number,
    rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ) => React.CSSProperties | undefined;

  getRowStyle?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => React.CSSProperties | undefined;

  enableZebra?: boolean;
  zebraColors?: ZebraColors;
  hoverHighlight?: boolean;
  dense?: boolean;
  softBorders?: boolean;
  stickyFirstCol?: boolean;

  atrasoColumnIndex?: number;

  atrasoColors?: {
    good?: string;
    warn?: string;
    bad?: string;
  };

  isRowPending?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => boolean;

  delayScope?: "cell" | "row";

  getRowGroup?: (
    rowData: (string | number | React.ReactNode)[],
    viewIndex: number,
    originalIndex: number
  ) => string | null;

  groupLabels?: { [groupKey: string]: string };
};

type SortConfig = { index: number | null; direction: "asc" | "desc" };

const Table: React.FC<TableProps> = ({
  headers,
  rows,
  columnWidths = [],
  responsiveWidths = {},
  onDoubleClick,

  filters: externalFilters,
  onFiltersChange,

  isTableStandby = false,
  osColumnIndex = 0,

  enableCellColors = false,
  getCellStyle,
  getRowStyle,

  enableZebra = true,
  zebraColors = { odd: "#fbfbfc", even: "#ffffff" },
  hoverHighlight = true,
  dense = false,
  softBorders = true,
  stickyFirstCol = false,

  atrasoColumnIndex,
  atrasoColors,

  isRowPending,
  delayScope = "cell",

  getRowGroup,
  groupLabels,

  selectedTextWhite = false,
}) => {
  const [internalFilters, setInternalFilters] = useState<string[]>(
    Array(headers.length).fill("")
  );

  const getInitialTableLayout = () => {
    const width = window.innerWidth;

    const sorted = Object.keys(responsiveWidths)
      .map(Number)
      .sort((a, b) => b - a);

    const bp = sorted.find((x) => width <= x) ?? null;

    const nextWidths = bp
      ? headers.map(
          (_, i) => responsiveWidths[bp][i + 1] || columnWidths[i] || "auto"
        )
      : columnWidths;

    const clamped = Math.max(12, Math.min(13, width / 135));

    return {
      widths: nextWidths,
      fontSize: clamped + "px",
    };
  };

  const filters = externalFilters ?? internalFilters;

  const updateFilters = (nextFilters: string[]) => {
    if (onFiltersChange) {
      onFiltersChange(nextFilters);
      return;
    }
    setInternalFilters(nextFilters);
  };

  const [selectedViewIndex, setSelectedViewIndex] = useState<number | null>(
    null
  );
  const [selectedOS, setSelectedOS] = useState<string | null>(null);
  const initialLayout = getInitialTableLayout();

  const [activeWidths, setActiveWidths] = useState<string[]>(
    initialLayout.widths
  );
  const [fontSize, setFontSize] = useState(initialLayout.fontSize);
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const [sort, setSort] = useState<SortConfig>({
    index: null,
    direction: "asc",
  });
  const tableRef = useRef<HTMLDivElement>(null);

  const { theme, departmentColor } = useTheme();

  const borderColor = softBorders ? "#d9dde3" : "#c7ccd4";
  const headerBg = "#f1f2f4";
  const headerText = "#454545";
  const filterBg = "#fafafa";
  const groupBarBg = "#eceef1";
  const groupBarText = "#7b8190";
  const footerBg = "#f6f7f9";
  const footerBorder = "#d5d9df";

  const baseSelectionColor = departmentColor || "#0a343d";

  const optionSelected = `${baseSelectionColor}C0`;
  const optionHover = `${baseSelectionColor}33`;

  const PENDING_BASE = "#fff6cf";
  const SELECTED_GREEN = optionSelected;
  const PENDING_SELECTED_SOLID = "#e8ddb0";
  const ROW_HOVER_BG = optionHover;

  const atrasoGood = atrasoColors?.good ?? "#dff0df";
  const atrasoWarn = atrasoColors?.warn ?? "#f1d59d";
  const atrasoBad = atrasoColors?.bad ?? "#d66a6a";

  const clamp = (value: number, min = 0, max = 255) =>
    Math.max(min, Math.min(max, value));

  const lightenHex = (hex: string, amount = 18) => {
    const normalized = hex.trim();
    const shortMatch = /^#([0-9a-f]{3})$/i.exec(normalized);
    const longMatch = /^#([0-9a-f]{6})$/i.exec(normalized);
    if (!shortMatch && !longMatch) return "#c1c7d0";

    let fullHex = normalized;

    if (shortMatch) {
      const s = shortMatch[1];
      fullHex = `#${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}`;
    }

    const value = fullHex.slice(1);
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);

    const nr = clamp(r + amount);
    const ng = clamp(g + amount);
    const nb = clamp(b + amount);

    return `#${nr.toString(16).padStart(2, "0")}${ng
      .toString(16)
      .padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
  };

  const sortIndicatorColor = useMemo(() => lightenHex("#8e95a3", 8), []);

  const sortIndicatorThickness = dense ? 2 : 3;

  useLayoutEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      const sorted = Object.keys(responsiveWidths)
        .map(Number)
        .sort((a, b) => b - a);

      const bp = sorted.find((x) => width <= x) ?? null;

      if (bp) {
        const conf = responsiveWidths[bp];
        const w = headers.map(
          (_, i) => conf[i + 1] || columnWidths[i] || "auto"
        );
        setActiveWidths(w);
      } else {
        setActiveWidths(columnWidths);
      }

      const clamped = Math.max(12, Math.min(13, width / 135));
      setFontSize(clamped + "px");
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [responsiveWidths, headers, columnWidths]);

  const parseMaybeNumber = (v: string): number | null => {
    const n = Number(
      v
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".")
    );
    return Number.isFinite(n) ? n : null;
  };

  const extractOS = (cell: string | number | React.ReactNode): string => {
    if (typeof cell === "string" || typeof cell === "number") {
      return String(cell);
    }

    if (
      cell &&
      typeof cell === "object" &&
      "props" in (cell as any) &&
      (cell as any).props?.os
    ) {
      return String((cell as any).props.os);
    }

    return "";
  };

  const getComparable = (cell: string | number | React.ReactNode) => {
    if (typeof cell === "number") return { kind: "num" as const, val: cell };

    if (typeof cell === "string") {
      const maybe = parseMaybeNumber(cell);
      if (maybe !== null) return { kind: "num" as const, val: maybe };
      return { kind: "str" as const, val: cell };
    }

    return { kind: "str" as const, val: "" };
  };

  const rowBaseBg = (i: number) =>
    enableZebra
      ? i % 2 === 0
        ? zebraColors.odd
        : zebraColors.even
      : "#ffffff";

  const { visibleRows, viewToOriginal } = useMemo(() => {
    const filtered: {
      row: (string | number | React.ReactNode)[];
      original: number;
    }[] = [];

    rows.forEach((row, originalIdx) => {
      const passes = row.every((cell, i) => {
        const f = (filters[i] ?? "").toLowerCase();
        if (!f) return true;

        const v =
          typeof cell === "string" || typeof cell === "number"
            ? String(cell).toLowerCase()
            : "";

        return v.includes(f);
      });

      if (passes) filtered.push({ row, original: originalIdx });
    });

    if (sort.index != null) {
      const i = sort.index;
      const m = sort.direction === "asc" ? 1 : -1;

      filtered.sort((a, b) => {
        const A = getComparable(a.row[i]);
        const B = getComparable(b.row[i]);

        if (A.kind === "num" && B.kind === "num") {
          if (A.val < B.val) return -1 * m;
          if (A.val > B.val) return 1 * m;
          return 0;
        }

        return (
          String(A.val).localeCompare(String(B.val), "pt-BR", {
            sensitivity: "base",
            numeric: true,
          }) * m
        );
      });
    }

    return {
      visibleRows: filtered.map((x) => x.row),
      viewToOriginal: filtered.map((x) => x.original),
    };
  }, [rows, filters, sort]);

  const selectRow = useCallback(
    (index: number | null) => {
      if (index == null || index < 0 || index >= visibleRows.length) return;

      const linha = visibleRows[index];

      if (isTableStandby) {
        const raw = linha[osColumnIndex];
        const osValue = extractOS(raw);
        setSelectedOS(osValue || null);
        setSelectedViewIndex(null);
      } else {
        setSelectedViewIndex(index);
        setSelectedOS(null);
      }

      const root = tableRef.current;
      if (root) {
        const trs = root.querySelectorAll("tbody tr[data-row='true']");
        if (trs[index]) {
          (trs[index] as HTMLElement).scrollIntoView({ block: "nearest" });
        }
      }
    },
    [visibleRows, isTableStandby, osColumnIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();

      if (!visibleRows.length) return;

      let current =
        isTableStandby && selectedOS
          ? visibleRows.findIndex(
              (r) => extractOS(r[osColumnIndex]) === selectedOS
            )
          : selectedViewIndex ?? -1;

      if (e.key === "ArrowDown") {
        current = Math.min(visibleRows.length - 1, current + 1);
      } else {
        current = Math.max(0, current - 1);
      }

      selectRow(current);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    visibleRows,
    selectedOS,
    selectedViewIndex,
    selectRow,
    isTableStandby,
    osColumnIndex,
  ]);

  const colorForDelay = (value: string | number | React.ReactNode) => {
    let n: number | null = null;

    if (typeof value === "number") n = Number.isFinite(value) ? value : null;
    else if (typeof value === "string") n = parseMaybeNumber(value);

    if (n == null) return null;

    let bg = atrasoBad;
    if (n <= 3) bg = atrasoGood;
    else if (n <= 9) bg = atrasoWarn;

    const fg = bg === atrasoBad ? "#fff" : "#111";
    return { bg, fg, n };
  };

  const looksLikeYellow = (c?: string) => {
    if (!c) return false;
    const x = c.toLowerCase().replace(/\s+/g, "");
    return (
      x.includes("#fff9d6") ||
      x.includes("#fffac8") ||
      x.includes("ffe79a") ||
      x.includes("fff7ed") ||
      x.includes("fffbeb")
    );
  };

  const isPendingRowComputed = (
    linha: (string | number | React.ReactNode)[],
    vIndex: number,
    oIndex: number
  ) => {
    if (isRowPending) return !!isRowPending(linha, vIndex, oIndex);
    if ((linha as any)?.__pendente_retorno === true) return true;

    if (enableCellColors && getCellStyle) {
      for (let col = 0; col < headers.length; col++) {
        const extra =
          getCellStyle(vIndex, col, linha[col], oIndex, linha, headers[col]) ||
          {};
        const bg =
          (extra.backgroundColor as string) || (extra.background as string);
        if (looksLikeYellow(bg)) return true;
      }
    }

    return false;
  };

  const headerHeight = dense ? "30px" : "34px";
  const filterHeight = dense ? "24px" : "28px";
  const cellHeight = dense ? "26px" : "28px";
  const cellPadding = dense ? "0 6px" : "0 8px";

  const activeColSpan = useMemo(
    () =>
      headers.reduce(
        (acc, _h, i) => (activeWidths[i] === "0px" ? acc : acc + 1),
        0
      ),
    [headers, activeWidths]
  );

  const footerTotals = useMemo(() => {
    return headers.map((_, colIndex) => {
      let hasNumeric = false;
      let total = 0;

      visibleRows.forEach((row) => {
        const cell = row[colIndex];
        if (typeof cell === "number") {
          total += cell;
          hasNumeric = true;
          return;
        }
        if (typeof cell === "string") {
          const n = parseMaybeNumber(cell);
          if (n !== null) {
            total += n;
            hasNumeric = true;
          }
        }
      });

      if (!hasNumeric) return "";
      return total.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    });
  }, [headers, visibleRows]);

  return (
    <div style={styles.wrapper}>
      <div ref={tableRef} style={styles.scroller}>
        <table
          style={{
            ...styles.table,
            fontFamily: "'Open Sans', sans-serif",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, index) =>
                activeWidths[index] !== "0px" ? (
                  <th
                    key={index}
                    onClick={() =>
                      setSort((prev) =>
                        prev.index === index
                          ? {
                              index,
                              direction:
                                prev.direction === "asc" ? "desc" : "asc",
                            }
                          : { index, direction: "asc" }
                      )
                    }
                    style={{
                      ...styles.th,
                      width: activeWidths[index],
                      backgroundColor: headerBg,
                      color: headerText,
                      fontSize,
                      height: headerHeight,
                      borderColor,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    role="button"
                    aria-sort={
                      sort.index === index
                        ? sort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    title="Clique para ordenar"
                  >
                    <div style={styles.thInner}>
                      <span style={styles.thText}>{header}</span>
                      <span style={styles.rightControls}>
                        {sort.index === index && sort.direction === "desc" && (
                          <span
                            aria-hidden="true"
                            style={{
                              ...styles.sortBarTop,
                              backgroundColor: sortIndicatorColor,
                              height: sortIndicatorThickness,
                            }}
                          />
                        )}
                        {sort.index === index && sort.direction === "asc" && (
                          <span
                            aria-hidden="true"
                            style={{
                              ...styles.sortBarBottom,
                              backgroundColor: sortIndicatorColor,
                              height: sortIndicatorThickness,
                            }}
                          />
                        )}
                      </span>
                    </div>
                  </th>
                ) : null
              )}
            </tr>
          </thead>

          <tbody>
            <tr>
              {headers.map((_, index) =>
                activeWidths[index] !== "0px" ? (
                  <td
                    key={`filter-${index}`}
                    style={{
                      ...styles.filterCell,
                      width: activeWidths[index],
                      height: filterHeight,
                      borderColor,
                      background: filterBg,
                      position:
                        index === 0 && stickyFirstCol ? "sticky" : undefined,
                      left: index === 0 && stickyFirstCol ? 0 : undefined,
                      zIndex: index === 0 && stickyFirstCol ? 2 : undefined,
                    }}
                  >
                    <input
                      type="text"
                      value={filters[index]}
                      onChange={(e) => {
                        const nf = [...filters];
                        nf[index] = e.target.value;
                        updateFilters(nf);
                        setSelectedOS(null);
                        setSelectedViewIndex(null);
                      }}
                      style={{
                        ...styles.filterInput,
                        fontSize,
                        height: dense ? "20px" : "22px",
                      }}
                    />
                  </td>
                ) : null
              )}
            </tr>

            {visibleRows.length > 0 &&
              (() => {
                const result: React.ReactNode[] = [];
                let lastGroupKey: string | null = null;

                visibleRows.forEach((linha, viewIndex) => {
                  const originalIndex = viewToOriginal[viewIndex];

                  const osValue = isTableStandby
                    ? extractOS(linha[osColumnIndex]) || null
                    : null;

                  const pendente = isPendingRowComputed(
                    linha,
                    viewIndex,
                    originalIndex
                  );

                  const prevRow = viewIndex - 1;
                  const nextRow = viewIndex + 1;

                  const isPrevSelected = isTableStandby
                    ? prevRow >= 0 &&
                      selectedOS != null &&
                      extractOS(visibleRows[prevRow]?.[osColumnIndex]) ===
                        selectedOS
                    : selectedViewIndex === prevRow;

                  const isNextSelected = isTableStandby
                    ? nextRow < visibleRows.length &&
                      selectedOS != null &&
                      extractOS(visibleRows[nextRow]?.[osColumnIndex]) ===
                        selectedOS
                    : selectedViewIndex === nextRow;

                  const isSelected = isTableStandby
                    ? selectedOS != null && osValue === selectedOS
                    : selectedViewIndex === viewIndex;

                  const base = pendente ? PENDING_BASE : rowBaseBg(viewIndex);
                  const baseOrHover =
                    hoverRow === viewIndex && !isSelected ? ROW_HOVER_BG : base;

                  const rowBaseLayer = `linear-gradient(0deg, ${baseOrHover}, ${baseOrHover})`;

                  const selectionLayer = isSelected
                    ? `linear-gradient(0deg, ${
                        pendente ? PENDING_SELECTED_SOLID : SELECTED_GREEN
                      }, ${pendente ? PENDING_SELECTED_SOLID : SELECTED_GREEN})`
                    : "";

                  const rowDelayLayer =
                    delayScope === "row" &&
                    typeof atrasoColumnIndex === "number"
                      ? (() => {
                          const val = linha[atrasoColumnIndex];
                          const info = colorForDelay(val);
                          return info
                            ? `linear-gradient(0deg, ${info.bg}, ${info.bg})`
                            : "";
                        })()
                      : "";

                  const rowLayers: string[] = [];
                  if (rowDelayLayer) rowLayers.push(rowDelayLayer);
                  if (selectionLayer) rowLayers.push(selectionLayer);
                  rowLayers.push(rowBaseLayer);

                  const groupKey =
                    getRowGroup?.(linha, viewIndex, originalIndex) || null;

                  const showGroupBar =
                    groupKey &&
                    groupLabels &&
                    groupLabels[groupKey] &&
                    groupKey !== lastGroupKey;

                  if (showGroupBar) {
                    lastGroupKey = groupKey;
                    result.push(
                      <tr key={`group-${groupKey}-${viewIndex}`}>
                        <td
                          colSpan={activeColSpan || headers.length}
                          style={{
                            ...styles.groupRow,
                            fontSize,
                            borderColor,
                          }}
                        >
                          {groupLabels[groupKey]}
                        </td>
                      </tr>
                    );
                  }

                  const externalRowStyle = getRowStyle?.(
                    linha,
                    viewIndex,
                    originalIndex
                  );

                  const externalRowBg =
                    (externalRowStyle?.backgroundColor as string) ||
                    (externalRowStyle?.background as string) ||
                    null;

                  const rowLayersWithExternal = [...rowLayers];
                  if (externalRowBg) {
                    rowLayersWithExternal.unshift(
                      `linear-gradient(0deg, ${externalRowBg}, ${externalRowBg})`
                    );
                  }

                  const sanitizedExternalRowStyle = externalRowStyle
                    ? { ...externalRowStyle }
                    : undefined;

                  if (sanitizedExternalRowStyle) {
                    delete sanitizedExternalRowStyle.background;
                    delete sanitizedExternalRowStyle.backgroundColor;
                  }

                  result.push(
                    <tr
                      data-row="true"
                      key={viewIndex}
                      onClick={() => selectRow(viewIndex)}
                      onDoubleClick={() =>
                        onDoubleClick?.(viewIndex, linha, originalIndex)
                      }
                      onMouseEnter={() =>
                        hoverHighlight && setHoverRow(viewIndex)
                      }
                      onMouseLeave={() => hoverHighlight && setHoverRow(null)}
                      style={{
                        cursor: "pointer",
                        backgroundImage: rowLayersWithExternal.join(", "),
                        backgroundOrigin: "padding-box",
                        backgroundClip: "padding-box",
                        ...sanitizedExternalRowStyle,
                      }}
                    >
                      {headers.map((headerTitle, colIndex) => {
                        if (activeWidths[colIndex] === "0px") return null;

                        const extra =
                          enableCellColors && getCellStyle
                            ? getCellStyle(
                                viewIndex,
                                colIndex,
                                linha[colIndex],
                                originalIndex,
                                linha,
                                headerTitle
                              ) || {}
                            : {};

                        let extraBg: string | null = null;
                        if (extra.backgroundColor) {
                          extraBg = String(extra.backgroundColor);
                        } else if (extra.background) {
                          extraBg = String(extra.background);
                        }

                        const normalizedExtraBg = extraBg
                          ?.toLowerCase()
                          .replace(/\s+/g, "");
                        const isDangerRow = normalizedExtraBg === "#ff47474c";

                        const isDangerHover =
                          isDangerRow && hoverRow === viewIndex && !isSelected;

                        const isDangerSelected = isDangerRow && isSelected;

                        const effectiveExtraBg = isDangerHover
                          ? "#F13A3AC1"
                          : isDangerSelected
                          ? "#F13A3ACA"
                          : extraBg;

                        const extraLayer = effectiveExtraBg
                          ? `linear-gradient(0deg, ${effectiveExtraBg}, ${effectiveExtraBg})`
                          : "";

                        const isDelayCell =
                          typeof atrasoColumnIndex === "number" &&
                          colIndex === atrasoColumnIndex;

                        const delayInfo = isDelayCell
                          ? colorForDelay(linha[colIndex])
                          : null;

                        const layersCell: string[] = [];

                        if (delayInfo && delayScope === "cell") {
                          layersCell.push(
                            `linear-gradient(0deg, ${delayInfo.bg}, ${delayInfo.bg})`
                          );
                        }

                        if (extraLayer) layersCell.push(extraLayer);
                        layersCell.push(...rowLayersWithExternal);

                        const fgColor =
                          isDangerHover || isDangerSelected
                            ? "#ffffff"
                            : selectedTextWhite && isSelected
                            ? "#ffffff"
                            : delayInfo && delayScope === "cell"
                            ? delayInfo.fg
                            : undefined;

                        const sanitizedCellStyle = { ...extra };
                        delete (sanitizedCellStyle as any).background;
                        delete (sanitizedCellStyle as any).backgroundColor;
                        delete (sanitizedCellStyle as any).color;

                        return (
                          <td
                            key={colIndex}
                            style={{
                              ...styles.td,
                              width: activeWidths[colIndex],
                              height: cellHeight,
                              boxShadow: isSelected
                                ? `
                                  inset 0 1px 0 rgba(0,0,0,0.55),
                                  inset 0 -1px 0 rgba(0,0,0,0.55),
                                  inset 1px 0 0 rgba(0,0,0,0.55),
                                  inset -1px 0 0 rgba(0,0,0,0.55)
                                `
                                : `
                                  inset 0 -1px 0 ${borderColor},
                                  inset -1px 0 0 ${borderColor}
                                `,
                              border: "none",
                              outline: "none",
                              fontSize,
                              padding: cellPadding,
                              position:
                                stickyFirstCol && colIndex === 0
                                  ? "sticky"
                                  : undefined,
                              left:
                                stickyFirstCol && colIndex === 0
                                  ? 0
                                  : undefined,
                              zIndex:
                                stickyFirstCol && colIndex === 0
                                  ? 1
                                  : undefined,
                              backgroundImage: layersCell.join(", "),
                              backgroundOrigin: "padding-box",
                              backgroundClip: "padding-box",
                              color: fgColor,
                              ...sanitizedCellStyle,
                            }}
                          >
                            <div
                              style={{
                                ...styles.cellInner,
                                color: fgColor,
                              }}
                            >
                              {linha[colIndex]}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                });

                return result;
              })()}

            {!visibleRows.length && (
              <tr>
                <td
                  colSpan={headers.length}
                  style={{
                    ...styles.emptyCell,
                    fontSize,
                    borderColor,
                  }}
                >
                  Nenhum registro encontrado com os filtros atuais.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          ...styles.bottomBar,
          background: footerBg,
          borderTop: `1px solid ${footerBorder}`,
        }}
      >
        <div style={styles.bottomLeft}>
          1 - {visibleRows.length} de {visibleRows.length} registros
        </div>

        <div style={styles.bottomRight}>
          <span style={styles.navBtn}>|◁</span>
          <span style={styles.navBtn}>◁</span>
          <span style={styles.pageDot}>1</span>
          <span style={styles.navMuted}>próximo ▷</span>
          <span style={styles.navBtn}>▷|</span>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    width: "100%",
    border: "1px solid #d5d9df",
    borderRadius: 0,
    overflow: "hidden",
    background: "#fff",
  },

  groupBar: {
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 600,
    fontSize: 12,
    borderBottom: "1px solid #d9dde3",
    letterSpacing: "0.01em",
  },

  scroller: {
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    background: "#fff",
  },

  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "100%",
    minWidth: "max-content",
  },

  th: {
    textAlign: "left",
    fontWeight: 700,
    borderRight: "1px solid",
    borderBottom: "1px solid",
    padding: 0,
    whiteSpace: "nowrap",
    userSelect: "none",
    cursor: "pointer",
    verticalAlign: "middle",
  },

  thInner: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    width: "100%",
    height: "100%",
    padding: "0 8px",
  },

  thText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  leftGrip: {
    fontSize: 10,
    opacity: 0.85,
    flexShrink: 0,
  },

  rightControls: {
    position: "relative",
    width: 18,
    minWidth: 18,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  filterIcon: {
    fontSize: 10,
    opacity: 0.55,
    lineHeight: 1,
  },

  sortBarTop: {
    position: "absolute",
    top: 2,
    left: 1,
    right: 1,
    borderRadius: 999,
    pointerEvents: "none",
  },

  sortBarBottom: {
    position: "absolute",
    bottom: 2,
    left: 1,
    right: 1,
    borderRadius: 999,
    pointerEvents: "none",
  },

  filterCell: {
    borderRight: "1px solid",
    borderBottom: "1px solid",
    padding: "0 6px",
  },

  filterInput: {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    fontFamily: "'Open Sans', sans-serif",
    textAlign: "center",
    color: "#7a8190",
    fontWeight: 600,
    textTransform: "uppercase",
  },

  td: {
    border: "none",
    fontWeight: 400,
    textAlign: "left",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },

  cellInner: {
    display: "block",
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#000000",
    fontFamily: "'Open Sans', sans-serif",
  },

  groupRow: {
    padding: "6px 10px",
    background: "#d8dde6",
    color: "#5f6775",
    fontWeight: 700,
    textAlign: "left",
    letterSpacing: "0.02em",
    borderBottom: "1px solid",
  },

  emptyCell: {
    padding: 16,
    textAlign: "center",
    color: "#7a8190",
    borderBottom: "1px solid",
    background: "#fff",
    fontFamily: "'Open Sans', sans-serif",
  },

  footerCell: {
    height: 28,
    borderRight: "1px solid",
    borderTop: "1px solid",
    padding: "0 8px",
    textAlign: "right",
    verticalAlign: "middle",
    color: "#707786",
    fontFamily: "'Open Sans', sans-serif",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  footerValue: {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  bottomBar: {
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 10px",
    fontFamily: "'Open Sans', sans-serif",
  },

  bottomLeft: {
    fontSize: 11,
    color: "#6f7683",
    fontWeight: 600,
  },

  bottomRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 11,
    color: "#9097a3",
  },

  navBtn: {
    opacity: 0.7,
    cursor: "pointer",
    userSelect: "none",
  },

  navMuted: {
    opacity: 0.7,
    userSelect: "none",
  },

  pageDot: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#cadf9b",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
  },
};

export default Table;
